import { useEffect, useState } from 'react';
import { Modal, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import type { PickupSlot, PreferredTiming } from '@/types';
import SlotPicker from './SlotPicker';
import PreferredTimingToggleRow from './PreferredTimingToggleRow';
import AutoSelectToggleRow from './AutoSelectToggleRow';
import RecurringToggleRow from './RecurringToggleRow';

export type SlotModalMode = 'mandatory' | 'preference' | 'same-day-only';

interface SlotModalProps {
  open: boolean;
  mode: SlotModalMode;
  title: string;
  subtitle: string;
  slots: PickupSlot[];
  isLoading: boolean;
  selectedCode?: string;
  onChangeSelected: (code: string) => void;
  // Single-step confirm (spec v1.1): passes BOTH the picked slot code and the
  // chosen preferred timing bucket — null = Anytime, else 'early'|'mid'|'late'.
  // Both optional so a caller that only cares about the slot (mandatory gate)
  // can ignore the second arg.
  onConfirm: (code?: string, preferredTiming?: PreferredTiming | null) => void;
  onCancel?: () => void;
  // §6.2 pre-populate — the merchant's currently-configured slot code and their
  // saved preferred TIMING (null = Anytime). When the picked slot ===
  // currentSlotCode the timing row seeds `currentPref`; picking any OTHER slot
  // resets it to Anytime (a bucket only means something relative to a slot).
  currentSlotCode?: string;
  currentPref?: PreferredTiming | null;
  isSaving?: boolean;
  // Extra guard the caller can set (e.g. slot + standing toggles unchanged) to
  // keep Confirm disabled. A timing-only change still re-enables it — SlotModal
  // owns the timing state, so it overrides this when the bucket is dirty.
  confirmDisabled?: boolean;
  // v2-task-9 (§4.1) — auto-select toggle, preference mode ONLY. Omitting
  // `onChangeAutoSelect` keeps the row unrendered even in preference mode.
  autoSelectEnabled?: boolean;
  onChangeAutoSelect?: (enabled: boolean) => void;
  autoSelectDisabled?: boolean;
  // v2-task-10fe (§4.4) — recurring-pickup toggle, preference mode ONLY. Same
  // opt-in gate: omitting `onChangeRecurring` keeps the row unrendered.
  recurringEnabled?: boolean;
  onChangeRecurring?: (enabled: boolean) => void;
  recurringDisabled?: boolean;
}

// Hairline divider between stacked rows inside the shared automation card.
// `divide-y` is unusable with Preflight off, so it is an explicit 1px strip.
const Hairline = () => <div className='h-px' style={{ background: 'rgb(232,230,227)' }} />;

// Single canonical visual shell shared by every slot-selection surface:
// the mandatory 422-gate modal (SlotSelectionModal), the banner's
// preference-mode modal (CurrentSlotBanner), and the rollover same-day-only
// "Change" modal (ChangeSlotModal, mode='respond'). Each caller owns its own
// data source/mutation and just renders this component — keeps the visuals
// (and the transitionName='' Chrome hidden-tab bugfix) in exactly one place.
//
// v1.1 collapsed the old two-step (slot → clock time) flow into ONE step:
// the merchant picks a slot, and — in preference mode — an optional
// "Preferred timing" toggle sits as a peer of auto-select/recurring in the
// same "Automation (optional)" strip, reusing SlotToggleRow so its switch is
// pixel-identical to the automation rows. No separate step, no Back button.
const SlotModal = ({
  open,
  mode,
  title,
  subtitle,
  slots,
  isLoading,
  selectedCode,
  onChangeSelected,
  onConfirm,
  onCancel,
  currentSlotCode,
  currentPref = null,
  isSaving = false,
  confirmDisabled = false,
  autoSelectEnabled = false,
  onChangeAutoSelect,
  autoSelectDisabled = false,
  recurringEnabled = false,
  onChangeRecurring,
  recurringDisabled = false,
}: SlotModalProps) => {
  const { t } = useTranslation();
  const closable = mode !== 'mandatory';

  // The chosen preferred-timing bucket for the picked slot, plus the value it
  // was seeded with, so a timing-only change can override a caller's
  // `confirmDisabled` (slot + standing toggles unchanged).
  const [preferredTiming, setPreferredTiming] = useState<PreferredTiming | null>(null);
  const [seededTiming, setSeededTiming] = useState<PreferredTiming | null>(null);

  // §6.2 — timing only means something relative to a slot: seed the SAVED
  // preference when the picked slot is the merchant's current one, otherwise
  // reset to Anytime.
  const seedTiming = (code?: string) => {
    const value = code && code === currentSlotCode ? (currentPref ?? null) : null;
    setPreferredTiming(value);
    setSeededTiming(value);
  };

  // Seed the timing whenever the modal opens OR the selected slot changes:
  // same slot as the merchant's current one → their saved bucket, any other
  // slot → Anytime. Keying on selectedCode (not just open) matters because
  // callers like ChangeSlotModal set selectedCode in their OWN effect, which
  // runs AFTER this child's — so an [open]-only effect would miss the prefill
  // and never seed the saved bucket. Reset on close so a reopen is clean.
  useEffect(() => {
    if (open) {
      seedTiming(selectedCode);
    } else {
      setPreferredTiming(null);
      setSeededTiming(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedCode, currentSlotCode, currentPref]);

  const selectedSlot = slots.find((slot) => slot.code === selectedCode);

  // Picking a slot just updates the caller's selection; the effect above
  // re-seeds the timing (same slot → saved bucket, different slot → Anytime).
  // No step advance — the timing row is inline below.
  const handleSlotChange = (code: string) => {
    onChangeSelected(code);
  };

  const handleConfirm = () => {
    if (!selectedCode) return;
    onConfirm(selectedCode, preferredTiming);
  };

  // A timing-only change (bucket differs from what it was seeded with) must
  // re-enable Confirm even when the caller reports slot + toggles unchanged.
  const timingDirty = preferredTiming !== seededTiming;
  const confirmBlocked = !selectedCode || isSaving || (confirmDisabled && !timingDirty);

  // The preferred-timing toggle shows when the merchant is choosing a slot they
  // own the timing of: the mandatory 422 gate and the preference modal. The
  // rollover same-day-only reshuffle ('respond') is a narrow "which slot today"
  // decision and stays free of it. The standing auto-select / recurring toggles
  // are preference-mode only and drive the "Automation (optional)" heading.
  const showTiming = mode !== 'same-day-only';
  const showAutomationToggles = mode === 'preference' && (!!onChangeAutoSelect || !!onChangeRecurring);

  return (
    <Modal
      open={open}
      centered
      width={460}
      closable={closable}
      maskClosable={closable}
      keyboard={closable}
      onCancel={closable ? onCancel : undefined}
      footer={null}
      className='slot-modal'
      // No zoom animation — Chrome pauses CSS animations in hidden/occluded
      // tabs, leaving rc-motion stuck in ant-zoom-*-active with
      // pointer-events:none (see SlotSelectionModal/ChangeSlotModal history).
      transitionName=''
      maskTransitionName=''
    >
      <div className='flex flex-none items-start justify-between pb-3'>
        <div>
          <h2 className='text-lg font-bold leading-tight'>{title}</h2>
          <p className='mt-1 text-xs text-[rgb(115,123,140)]'>{subtitle}</p>
        </div>
      </div>

      {/* PRIMARY — the slot list leads, directly under the header. Picking a
          time window is the merchant's actual job, so it gets the prime space
          and the visual weight; the automation options are demoted below. It
          is the ONLY scrollable region (`flex-1 min-h-0`) with an explicit
          `maxHeight` cap so the automation strip + footer below can never be
          overlapped (the reported S5-clipped-behind-footer bug). `p-1` keeps
          the selected row's 2px border clear of the scroll clip edge. */}
      {isLoading ? (
        <div className='flex flex-none justify-center py-8'>
          <Spin />
        </div>
      ) : (
        <div className='flex-1 min-h-0 space-y-2 overflow-y-auto p-1' style={{ maxHeight: '44vh' }}>
          <SlotPicker
            slots={slots}
            value={selectedCode}
            onChange={handleSlotChange}
            sameDayOnly={mode === 'same-day-only'}
          />
        </div>
      )}

      {/* SECONDARY — the compact toggle strip BELOW the slot list. One card of
          stacked ONE-LINE rows split by hairlines, kept clearly subordinate to
          the slots. Preferred timing leads and shows in EVERY mode (it's the
          per-pickup choice for the slot just picked); the standing auto-select
          / recurring toggles are preference-mode only and, when present, add
          the "Automation (optional)" heading. Each is gated by its own handler
          so a caller that hasn't wired one never gets it for free. */}
      {!isLoading && showTiming ? (
        <div className='flex-none pt-3'>
          {showAutomationToggles ? (
            <p className='mb-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-wide text-[rgb(115,123,140)]'>
              {String(t('automation_optional_heading'))}
            </p>
          ) : null}
          <div
            className='overflow-hidden rounded-xl border border-solid'
            style={{ borderColor: 'rgb(232,230,227)' }}
          >
            <PreferredTimingToggleRow
              value={preferredTiming}
              onChange={setPreferredTiming}
              bordered={false}
              compact
            />
            {showAutomationToggles && onChangeAutoSelect ? (
              <>
                <Hairline />
                <AutoSelectToggleRow
                  slots={slots}
                  checked={autoSelectEnabled}
                  onChange={onChangeAutoSelect}
                  disabled={autoSelectDisabled}
                  bordered={false}
                  compact
                />
              </>
            ) : null}
            {showAutomationToggles && onChangeRecurring ? (
              <>
                <Hairline />
                <RecurringToggleRow
                  checked={recurringEnabled}
                  onChange={onChangeRecurring}
                  disabled={recurringDisabled}
                  bordered={false}
                  compact
                />
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Footer: balanced full-width button pair (each flex-1, 40px tall). NO
          divider line — a top border here read as a stray rule slicing across
          the button tops; the whitespace above (mt-4) is enough separation and
          matches every other button pair in the app. */}
      <div className='mt-4 flex flex-none items-center gap-3'>
        {mode !== 'mandatory' ? (
          <button
            type='button'
            onClick={onCancel}
            className='h-10 flex-1 rounded-lg border border-solid text-sm font-semibold transition-colors hover:bg-[rgb(245,245,243)] active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]'
            style={{ borderColor: 'rgb(232,230,227)' }}
          >
            {String(t('cancel'))}
          </button>
        ) : null}
        <button
          type='button'
          disabled={confirmBlocked}
          onClick={handleConfirm}
          className='inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg bg-[rgb(237,224,74)] text-sm font-semibold text-[rgb(57,57,60)] transition-colors hover:bg-[rgb(230,217,60)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]'
        >
          {isSaving ? <Spin size='small' /> : null}
          {String(t('confirm_slot'))}
        </button>
      </div>
    </Modal>
  );
};

export default SlotModal;
